import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef, useMemo } from "react";
import { fadeInUp, staggerContainer, scaleOnHover } from "../lib/animations";
import WebGL3DBackground from "../components/WebGL3DBackground";
import Interactive3DHero from "../components/Interactive3DHero";
import FloatingTechIcons3D from "../components/FloatingTechIcons3D";
import Footer from "../components/Footer";
import Navigation from "@/components/Navigation";
export default function Home() {

  const servicesRef = useRef<HTMLDivElement>(null);

  const heroContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const heroFadeUp = {
  hidden: { opacity: 0, y: 18, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const heroFade = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.7 } },
};

  
  const heroSlides = [
    {
      title: "AI-Powered",
      subtitle: "Digital Transformation",
      description: "Harness artificial intelligence and machine learning to revolutionize your business operations with intelligent automation and predictive analytics.",
      image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      cta: "Transform Now",
      stats: "Automation-first delivery"
    },
    {
      title: "Immersive",
      subtitle: "User Experiences", 
      description: "Create captivating digital experiences with cutting-edge UI/UX design, progressive web apps, and next-generation mobile interfaces.",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      cta: "Experience Innovation",
      stats: "Human-centered UX"
    },
    {
      title: "Quantum-Ready",
      subtitle: "Cloud Architecture",
      description: "Future-proof your infrastructure with quantum-safe security, edge computing, and hyper-scalable microservices that adapt to tomorrow's demands.",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
      cta: "Build the Future",
      stats: "Scalable cloud systems"
    }
  ];

  const companyStats = [
    { value: "500+", label: "Projects Delivered", icon: "fas fa-laptop-code", description: "Web, CRM, ERP and custom applications" },
    { value: "200+", label: "Happy Clients", icon: "fas fa-users-cog", description: "Long-term relationships across industries" },
    { value: "10+", label: "Years Experience", icon: "fas fa-shield-alt", description: "Stable systems, support and maintenance" },
    { value: "24/7", label: "Support Availability", icon: "fas fa-headset", description: "Help for critical business workflows" }
  ];
function CountUp({
  value,
  duration = 900,
}: {
  value: string; // "500+", "4.9/5", "24/7"
  duration?: number;
}) {
  const [out, setOut] = useState(value);

  const parsed = useMemo(() => {
    // Extract first number (int/float) from string
    const m = value.match(/(\d+(\.\d+)?)/);
    if (!m) return null;

    const num = parseFloat(m[1]);
    const isPercent = value.includes("%");
    const suffix = value.replace(m[1], ""); // keeps "+", "%", "/7", etc.
    const decimals = m[1].includes(".") ? m[1].split(".")[1].length : 0;

    return { num, isPercent, suffix, decimals };
  }, [value]);

  useEffect(() => {
    if (!parsed) return;

    let raf = 0;
    const start = performance.now();
    const from = 0;

    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      const cur = from + (parsed.num - from) * eased;

      const formatted = cur.toFixed(parsed.decimals);
      setOut(`${formatted}${parsed.suffix}`);

      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [parsed, duration]);

  return <>{out}</>;
}
const services = [
  {
    title: "Best Custom Software Development",
    subtitle: "Web apps, portals, dashboards and APIs",
    description: "Build secure, scalable business software around your workflow. We plan, design, develop and support web applications that help teams move faster.",
    tags: ["React", "Node.js", ".NET", "PHP", "PostgreSQL"],
    icon: "fas fa-laptop-code",
    image: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
    gradient: "from-blue-500 to-cyan-500",
    stats: "500+ Projects Delivered"
  },
  {
    title: "Top CRM & ERP Development",
    subtitle: "Sales, billing, inventory and operations",
    description: "Create CRM and ERP systems that match your process instead of forcing your team into a rigid product. Add lead tracking, reports, roles and integrations.",
    tags: ["CRM", "ERP", "Lead Management", "Billing", "Reports"],
    icon: "fas fa-users",
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
    gradient: "from-green-500 to-teal-500",
    stats: "2500+ Active Clients"
  },
  {
    title: "AI Automation & Data Analytics",
    subtitle: "Smarter workflows and decision dashboards",
    description: "Use AI-assisted automation, dashboards and data pipelines to reduce manual work, improve follow-ups and turn business data into clear decisions.",
    tags: ["AI Automation", "BI Dashboards", "Chatbots", "Reports"],
    icon: "fas fa-brain",
    image: "https://images.unsplash.com/photo-1502813152688-d5f3b9a38e5d?ixid=MnwzNjUyOXwwfDF8c2VhY2h8N3x8Y3J5fGZhbHNlfGVufDB8fHx8fHw%3D&ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
    gradient: "from-orange-500 to-yellow-500",
    stats: "Automation-first delivery"
  },
{
  title: "Cloud Call Center, IVR & Dialer",
  subtitle: "Customer communication automation",
  description: "Launch call center workflows with dialer automation, IVR, call routing, WhatsApp integration and CRM sync for sales and support teams.",
  tags: ["IVR", "Dialer", "VoIP", "WhatsApp API", "CRM Sync"],
  icon: "fas fa-phone-alt  rotate-[95deg]" ,  // Ensure proper icon rendering by using Font Awesome
  image: "https://images.unsplash.com/photo-1600385029078-5f89b6d25b82?crop=entropy&cs=tinysrgb&fit=max&ixid=MnwzNjUyOXwwfDF8c2VhY2h8NjN8fGZhbHNlX3BvcnRhbHxlbnwwfHx8fDE2NjU1NzU2NzA&ixlib=rb-1.2.1&q=80&w=400",
  gradient: "from-indigo-500 to-blue-500",
  stats: "Calling automation"
},
 {
    title: "Mobile App Development Services",
    subtitle: "Android, iOS and cross-platform apps",
    description: "Turn business ideas into polished mobile apps with reliable APIs, clean UI, payment flows, notifications and ongoing optimization.",
    tags: ["Flutter", "React Native", "Android", "iOS", "APIs"],
    icon: "fas fa-mobile-alt",
    image: "https://images.unsplash.com/photo-1562185647-51a9c0a75529?crop=entropy&cs=tinysrgb&fit=max&ixid=MXwyMDg2MnwwfDF8c2VhY2h8NXx8Y2xvdWQgdXNlcnxlbnwwfHx8fDE2NjYyOTQxNzA&ixlib=rb-1.2.1&q=80&w=400",
    gradient: "from-pink-500 to-red-500",
    stats: "Mobile delivery"
  },
  {
    title: "SEO & Digital Growth Services",
    subtitle: "Search-ready sites and growth campaigns",
    description: "Improve visibility with SEO-ready development, technical SEO, content structure, landing pages and conversion-focused digital growth support.",
    tags: ["SEO", "Landing Pages", "Technical SEO", "Content", "Analytics"],
    icon: "fas fa-chart-line",
    image: "https://images.unsplash.com/photo-1560749031-3e25a04e39f1?crop=entropy&cs=tinysrgb&fit=max&ixid=MXwyMDg2MnwwfDF8c2VhY2h8NjV8fGZhbHNlX3BvcnRhbHxlbnwwfHx8fDE2NjY2MTY5MTk&ixlib=rb-1.2.1&q=80&w=400",
    gradient: "from-purple-500 to-indigo-500",
    stats: "Search-focused builds"
  }
];

function ServicesWowPro({ services }: { services: any[] }) {
  const safeServices = useMemo(() => (services?.length ? services : []), [services]);
  const [active, setActive] = useState(0);

  const a = safeServices[active] ?? safeServices[0];
  if (!a) return null;

  return (
    <div className="grid lg:grid-cols-12 gap-8 items-start">
      {/* LEFT: Holo Preview (device card) */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65 }}
        viewport={{ once: true, amount: 0.25 }}
        className="lg:col-span-5 lg:sticky lg:top-24 space-y-6"
      >
        <div className="relative rounded-[34px] border border-white/10 bg-white/[0.06] backdrop-blur-xl overflow-hidden shadow-[0_50px_140px_-70px_rgba(0,0,0,0.95)]">
          {/* outer glow ring */}
          <motion.div
            aria-hidden
            className="absolute -inset-[2px] opacity-70 blur-2xl"
            style={{
              background:
                "conic-gradient(from_220deg_at_50%_50%,rgba(34,211,238,0.0),rgba(34,211,238,0.34),rgba(59,130,246,0.26),rgba(99,102,241,0.22),rgba(236,72,153,0.16),rgba(34,211,238,0.0))",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 85, repeat: Infinity, ease: "linear" }}
          />

          {/* inner glass */}
          <div className="absolute inset-0 bg-slate-950/55" />

          {/* moving sheen */}
          <motion.div
            aria-hidden
            className="absolute -left-24 top-0 h-full w-24 rotate-12 opacity-30"
            style={{
              background:
                "linear-gradient(to right, transparent, rgba(255,255,255,0.35), transparent)",
            }}
            animate={{ x: ["-30%", "140%"] }}
            transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", repeatDelay: 1.2 }}
          />

          {/* scanline */}
          <motion.div
            aria-hidden
            className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-300/40 to-transparent opacity-40"
            animate={{ y: [60, 420, 60] }}
            transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
          />

          <div className="relative p-8">
            {/* top row */}
            <div className="flex items-start justify-between gap-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/70">
                <span className="relative h-2 w-2">
                  <span className="absolute inset-0 rounded-full bg-cyan-300/60 animate-ping" />
                  <span className="absolute inset-0 rounded-full bg-cyan-300" />
                </span>
                Featured Service
              </div>

              <div className="hidden sm:flex items-center gap-2 text-xs text-white/55">
                <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
                Hover / Click cards
                <span className="opacity-80">{"->"}</span>
              </div>
            </div>

            {/* content swap */}
            <AnimatePresence mode="wait">
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 12, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -10, filter: "blur(8px)" }}
                transition={{ duration: 0.35 }}
                className="mt-6"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`h-14 w-14 rounded-2xl bg-gradient-to-r ${a.gradient} flex items-center justify-center shadow-[0_20px_40px_-22px_rgba(0,0,0,0.9)]`}
                  >
                    <i className={`${a.icon} text-xl text-white`} />
                  </div>

                  <div>
                    <h3 className="text-2xl font-extrabold text-white">{a.title}</h3>
                    <p className="mt-1 text-sm font-semibold text-white/70">{a.subtitle}</p>
                  </div>
                </div>

                {/* â€œdevice screenâ€ */}
                <div className="mt-5 rounded-3xl border border-white/10 bg-black/25 p-5">
                  <p className="text-white/70 leading-relaxed">{a.description}</p>

                  {/* stats + chips row */}
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/75">
                      <span className={`h-2 w-2 rounded-full bg-gradient-to-r ${a.gradient}`} />
                      {a.stats}
                    </span>

                    {["Fast delivery", "Enterprise security", "Scalable infra"].map((x) => (
                      <span
                        key={x}
                        className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/65"
                      >
                        {x}
                      </span>
                    ))}
                  </div>

                  {/* tags */}
                  <div className="mt-5 flex flex-wrap gap-2">
                    {a.tags.slice(0, 6).map((t: string) => (
                      <span
                        key={t}
                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA row */}
             
              </motion.div>
            </AnimatePresence>
          </div>

          {/* bottom strip */}
          <div className="relative border-t border-white/10 px-8 py-5 flex items-center justify-between text-xs text-white/60">
            <span>Enterprise-ready | Secure | Scalable</span>
          </div>
        </div>

        {/* mini â€œtimeline / processâ€ to make left feel fuller */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { k: "Designing for Engagement", v: "UI/UX Design" },
            { k: "Secure Data Migration", v: "Data Transfer" },
            { k: "Efficient API Integrations", v: "API Setup" },
          ].map((x) => (
            <div
              key={x.k}
              className="rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-xl p-4"
            >
              <div className="text-xs uppercase tracking-widest text-white/55">{x.k}</div>
              <div className="mt-1 text-sm font-semibold text-white/80">{x.v}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* RIGHT: Cards (more premium) */}
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
        className="lg:col-span-7 grid sm:grid-cols-2 gap-6"
      >
        {safeServices.map((s, idx) => {
          const isActive = idx === active;
          return (
            <motion.button
              key={s.title}
              type="button"
              onMouseEnter={() => setActive(idx)}
              onFocus={() => setActive(idx)}
              onClick={() => setActive(idx)}
              variants={{
                hidden: { opacity: 0, y: 16, scale: 0.98 },
                show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.55 } },
              }}
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 240, damping: 18 }}
              className={[
                "group text-left relative rounded-[30px] border backdrop-blur-xl p-6 overflow-hidden",
                "shadow-[0_34px_110px_-70px_rgba(0,0,0,0.95)]",
                isActive
                  ? "border-cyan-300/30 bg-white/[0.09]"
                  : "border-white/10 bg-white/[0.05] hover:bg-white/[0.07]",
              ].join(" ")}
            >
              {/* always-on micro gradient for depth */}
              <div className="pointer-events-none absolute inset-0 opacity-60 bg-[radial-gradient(600px_circle_at_20%_10%,rgba(255,255,255,0.08),transparent_55%)]" />

              {/* hover glow */}
              <div
                className={[
                  "pointer-events-none absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl",
                  `bg-gradient-to-r ${s.gradient}`,
                ].join(" ")}
              />

              {/* active ring */}
              {isActive && (
                <div className="pointer-events-none absolute inset-0 rounded-[30px] ring-2 ring-cyan-300/20" />
              )}

              {/* top line */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

              <div className="relative">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-11 w-11 rounded-2xl bg-gradient-to-r ${s.gradient} flex items-center justify-center shadow-lg`}
                    >
                      <i className={`${s.icon} text-lg text-white`} />
                    </div>

                    <div>
                      <div className="text-sm font-extrabold text-white">{s.title}</div>
                      <div className="text-xs text-white/60 font-semibold mt-1">{s.subtitle}</div>
                    </div>
                  </div>

                  <span
                    className={[
                      "h-2.5 w-2.5 rounded-full mt-2",
                      isActive
                        ? "bg-cyan-300 shadow-[0_0_0_10px_rgba(34,211,238,0.10)]"
                        : "bg-white/25",
                    ].join(" ")}
                  />
                </div>

                <p className="mt-4 text-sm text-white/70 leading-relaxed line-clamp-3">
                  {s.description}
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {s.tags.slice(0, 3).map((t: string) => (
                    <span
                      key={t}
                      className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/65"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between text-xs text-white/60">
                  <span className="inline-flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full bg-gradient-to-r ${s.gradient}`} />
                    {s.stats}
                  </span>

              
                </div>
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
}
  return (
  
    <div className="min-h-screen bg-white dark:bg-slate-900" style={{ fontFamily: 'Roboto, sans-serif' }}>
      <Navigation/>
{/* Hero Section */}
<section className="relative overflow-hidden bg-[#050B1D] text-white">
  <div className="absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_10%,rgba(59,130,246,0.26),transparent_55%),radial-gradient(900px_circle_at_85%_75%,rgba(14,165,233,0.20),transparent_55%),linear-gradient(to_bottom,rgba(2,6,23,0.90),rgba(2,6,23,1))]" />
    <div className="absolute inset-0 opacity-10 [background-image:linear-gradient(rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.10)_1px,transparent_1px)] [background-size:60px_60px] [mask-image:radial-gradient(circle_at_center,black_30%,transparent_70%)]" />
  </div>

  <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
    <motion.div
      variants={heroContainer}
      initial="hidden"
      animate="show"
      className="grid min-h-[640px] items-center gap-12 lg:grid-cols-[1.02fr_0.98fr]"
    >
      {/* LEFT */}
      <div className="px-0 text-center sm:px-4 lg:pl-8 lg:text-left">
        <motion.div
          variants={heroFadeUp}
          className="inline-flex max-w-full flex-wrap items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 shadow-[0_12px_40px_-24px_rgba(59,130,246,0.75)] backdrop-blur-md sm:rounded-full lg:justify-start"
        >
          <span className="relative h-2 w-2">
            <span className="absolute inset-0 animate-ping rounded-full bg-sky-400 opacity-60" />
            <span className="absolute inset-0 rounded-full bg-sky-400" />
          </span>
          <span>Empowering Future-Ready Solutions</span>
          <span className="rounded-full border border-sky-400/20 bg-sky-500/15 px-2 py-0.5 text-xs font-bold text-sky-200">
            AI &bull; ML &bull; Automation
          </span>
        </motion.div>

        <motion.h1
          variants={heroFadeUp}
          className="mt-6 max-w-4xl text-center text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-left lg:text-5xl"
        >
          Custom Software Development{" "}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-sky-400 via-blue-200 to-indigo-300 bg-clip-text text-transparent drop-shadow-[0_0_34px_rgba(56,189,248,0.25)]">
              Company in India
            </span>
            <span className="pointer-events-none absolute -bottom-2 left-0 h-[2px] w-full overflow-hidden rounded-full bg-white/10">
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/45 to-transparent"
                initial={{ x: "-60%" }}
                animate={{ x: "60%" }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
              />
            </span>
          </span>
          <span className="mt-2 block text-3xl font-bold text-white/80 sm:text-4xl lg:text-5xl">
            Grow Faster
          </span>
        </motion.h1>

        <motion.p
          variants={heroFadeUp}
          className="mx-auto mt-5 max-w-xl text-center text-base leading-relaxed text-white/70 sm:text-lg lg:mx-0 lg:text-left"
        >
          Variable Soft builds custom CRM, ERP, mobile apps, web apps, AI automation,
          web scraping systems, IVR, dialer and business workflow software for
          companies in India and worldwide.
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={heroFadeUp}
          className="mt-8 flex flex-col justify-center gap-4 sm:flex-row lg:justify-start"
        >
          <a
            href="/getintouch"
            className="group relative inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 to-indigo-500 px-5 py-3 font-semibold text-white shadow-[0_16px_40px_-16px_rgba(56,189,248,0.5)] transition hover:-translate-y-0.5 hover:brightness-110"
          >
            <span className="absolute -inset-[1px] rounded-2xl bg-[radial-gradient(420px_circle_at_30%_30%,rgba(56,189,248,0.25),transparent_45%)] opacity-0 blur-xl transition group-hover:opacity-100" />
            <span className="relative">Start Your Journey</span>
            <span className="relative opacity-80 transition group-hover:translate-x-1">{"->"}</span>
          </a>

          <a
            href="/service"
            className="group relative inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-transparent px-6 py-3 font-semibold text-white shadow-[0_12px_32px_-14px_rgba(56,189,248,0.35)] transition hover:-translate-y-0.5 hover:bg-white/10"
          >
            <span className="absolute -inset-[1px] rounded-2xl bg-[radial-gradient(420px_circle_at_30%_30%,rgba(56,189,248,0.25),transparent_45%)] opacity-0 blur-xl transition group-hover:opacity-100" />
            <span className="relative">Our Services</span>
          </a>
        </motion.div>

        {/* Trust row */}
        <motion.div
          variants={heroFade}
          className="mt-10 flex flex-wrap items-center justify-center gap-3 text-sm text-white/70 lg:justify-start"
        >
          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md transition hover:bg-white/10">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className="fas fa-star text-xs text-yellow-400" />
              ))}
            </div>
            <span className="font-semibold text-white">5.0/5.0</span>
          </div>
          <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md transition hover:bg-white/10">
            500+ Successful Projects
          </div>
          <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md transition hover:bg-white/10">
            Support: 24/7
          </div>
        </motion.div>

        <motion.div
          variants={heroFade}
          className="mt-7 flex max-w-2xl flex-wrap justify-center gap-2 text-sm font-semibold lg:justify-start"
        >
          {[
            "Best Software Development Company",
            "Top Web Development Services",
            "AI Automation Services",
            "CRM & ERP Development",
            "WhatsApp Business API",
            "Micro-GCC India",
          ].map((item) => (
            <span
              key={item}
              className="rounded-full border border-white/75 bg-white px-4 py-2 text-slate-700 shadow-[0_14px_34px_-24px_rgba(15,23,42,0.7)] backdrop-blur transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-blue-700"
            >
              {item}
            </span>
          ))}
        </motion.div>
      </div>

      {/* RIGHT */}
      <motion.div variants={heroFadeUp} className="relative">
        <div className="rounded-[32px] border border-slate-200 bg-white p-3 shadow-[0_35px_90px_-50px_rgba(15,23,42,0.45)]">
          <div className="overflow-hidden rounded-[24px] border border-slate-100 bg-slate-950">
            <div className="flex items-center justify-between border-b border-white/10 bg-slate-900 px-5 py-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Future-ready command center
                </div>
                <div className="mt-1 text-lg font-black text-white">
                  AI, software and automation stack
                </div>
              </div>
              <span className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1.5 text-xs font-bold text-cyan-200">
                AI-ready
              </span>
            </div>

            <div className="grid gap-0 lg:grid-cols-[0.95fr_1.05fr]">
              <div className="border-b border-white/10 p-5 lg:border-b-0 lg:border-r">
                <div className="space-y-3">
                  {[
                    { icon: "fas fa-brain", label: "AI strategy", text: "Use cases, data, automation scope" },
                    { icon: "fas fa-code", label: "Software build", text: "Web apps, APIs, CRM and portals" },
                    { icon: "fas fa-robot", label: "Workflow automation", text: "Agents, bots, WhatsApp and reports" },
                    { icon: "fas fa-chart-line", label: "Growth systems", text: "SEO, analytics and conversion flows" },
                  ].map((step, index) => (
                    <div key={step.label} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 text-white">
                        <i className={`${step.icon} text-xs`} />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-white">
                          {step.label}
                        </div>
                        <div className="mt-0.5 text-xs leading-relaxed text-slate-400">
                          {step.text}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-50 p-5">
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
                        Business transformation layer
                      </div>
                      <div className="text-sm font-black text-slate-900">AI + software + growth workflows</div>
                    </div>
                    <div className="flex gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-300" />
                      <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                    </div>
                  </div>
                  <div className="relative h-56 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-4">
                    <div className="grid h-full grid-cols-2 gap-3">
                      {[
                        { label: "Lead AI", value: "92%", icon: "fas fa-comments" },
                        { label: "Automation", value: "24/7", icon: "fas fa-bolt" },
                        { label: "Delivery", value: "Agile", icon: "fas fa-code-branch" },
                        { label: "Growth", value: "SEO+", icon: "fas fa-chart-simple" },
                      ].map((metric) => (
                        <div key={metric.label} className="rounded-2xl border border-white/10 bg-white/[0.07] p-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-white/60">{metric.label}</span>
                            <i className={`${metric.icon} text-cyan-200 text-xs`} />
                          </div>
                          <div className="mt-4 text-2xl font-black text-white">{metric.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  {[
                    { value: "AI", label: "Smart responses" },
                    { value: "ML", label: "Predictive insights" },
                    { value: "API", label: "Connected systems" },
                    { value: "GCC", label: "Dedicated teams" },
                  ].map((item) => (
                    <div key={item.value} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="text-lg font-black text-blue-700">{item.value}</div>
                      <div className="mt-1 text-xs font-semibold text-slate-500">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-white/10 bg-slate-900 px-5 py-4">
              {["AI Agents", "RAG", "React", "Node", "Python", "PostgreSQL", "Cloud"].map((tech) => (
                <span key={tech} className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-xs font-semibold text-slate-300">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  </div>
</section>

      {/* Service Focus Strip */}
<section className="relative z-10 -mt-10 px-4 sm:px-6 lg:px-8">
  <div className="mx-auto max-w-7xl rounded-[28px] border border-slate-200/70 bg-white/90 p-4 shadow-[0_30px_90px_-55px_rgba(15,23,42,0.45)] backdrop-blur-xl">
    <div className="grid gap-3 md:grid-cols-4">
      {[
        {
          title: "Custom Software",
          desc: "Web apps, dashboards, APIs and portals",
          icon: "fas fa-code",
          accent: "from-sky-500 to-blue-600",
          bg: "from-sky-50 via-white to-blue-50",
          ring: "group-hover:border-sky-200",
        },
        {
          title: "CRM / ERP Systems",
          desc: "Lead, sales, billing and operations workflows",
          icon: "fas fa-layer-group",
          accent: "from-indigo-500 to-violet-600",
          bg: "from-indigo-50 via-white to-violet-50",
          ring: "group-hover:border-indigo-200",
        },
        {
          title: "Automation Stack",
          desc: "Dialer, IVR, WhatsApp, bots and reports",
          icon: "fas fa-bolt",
          accent: "from-cyan-500 to-teal-500",
          bg: "from-cyan-50 via-white to-teal-50",
          ring: "group-hover:border-cyan-200",
        },
        {
          title: "Dedicated Teams",
          desc: "Micro-GCC pods for long-term delivery",
          icon: "fas fa-users-cog",
          accent: "from-blue-600 to-slate-900",
          bg: "from-blue-50 via-white to-slate-100",
          ring: "group-hover:border-blue-200",
          href: "/services/micro-gcc-india",
        },
      ].map((item) => {
        const CardTag = item.href ? "a" : "div";

        return (
        <CardTag
          key={item.title}
          href={item.href}
          className={`group relative overflow-hidden rounded-2xl border border-slate-100 bg-gradient-to-br ${item.bg} p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_45px_-30px_rgba(15,23,42,0.45)] ${item.ring}`}
        >
          <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${item.accent}`} />
          <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/60 blur-2xl transition group-hover:scale-125" />

          <div className="relative flex items-start gap-4">
            <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br ${item.accent} text-white shadow-lg shadow-blue-500/15`}>
              <i className={`${item.icon} text-sm`} />
            </div>
            <div>
              <div className="text-sm font-black text-slate-950">{item.title}</div>
              <div className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</div>
            </div>
          </div>
        </CardTag>
        );
      })}
    </div>
  </div>
</section>

      {/* Micro-GCC / Dedicated Teams Section */}
<section className="relative overflow-hidden bg-slate-50 py-16 md:py-24">
  <div className="absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_15%_20%,rgba(59,130,246,0.12),transparent_55%),radial-gradient(760px_circle_at_90%_80%,rgba(20,184,166,0.10),transparent_55%)]" />
    <div className="absolute inset-0 opacity-[0.22] [background-image:linear-gradient(rgba(15,23,42,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.06)_1px,transparent_1px)] [background-size:48px_48px]" />
  </div>

  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid lg:grid-cols-12 gap-10 items-center">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.6 }}
        className="lg:col-span-6"
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white/80 px-4 py-2 text-xs font-semibold tracking-widest text-primary shadow-sm">
          MICRO-GCC AND DEDICATED TEAMS
        </span>

        <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-950 leading-tight">
          Build your India software team with Variable Soft.
        </h2>

        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          For service-based clients who need long-term engineering capacity, we help set up
          dedicated offshore teams and Micro-GCC pods in India. Start with 3 to 5 people
          across development, QA, CRM, automation or support, then scale when the model is proven.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {[
            "Custom software and SaaS teams",
            "CRM, ERP and workflow automation",
            "BOT Lite setup option",
            "Sprint-based delivery governance",
          ].map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/85 p-4 shadow-sm">
              <span className="mt-1 h-2.5 w-2.5 rounded-full bg-primary" />
              <span className="text-sm font-semibold text-slate-700">{item}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a
            href="/services/micro-gcc-india"
            className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 font-semibold text-white shadow-lg hover:bg-slate-800 transition"
          >
            Explore Micro-GCC Model
          </a>
          <a
            href="/getintouch"
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 font-semibold text-slate-900 shadow-sm hover:border-primary/40 transition"
          >
            Discuss Your Team
          </a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="lg:col-span-6"
      >
        <div className="relative rounded-[30px] border border-slate-200 bg-white/90 p-5 sm:p-7 shadow-[0_40px_100px_-55px_rgba(15,23,42,0.45)]">
          <div className="absolute -inset-4 -z-10 rounded-[36px] bg-gradient-to-r from-blue-500/15 via-cyan-400/10 to-teal-400/15 blur-2xl" />

          <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-5">
            <div>
              <p className="text-xs font-semibold tracking-widest text-slate-500">TEAM MODEL</p>
              <h3 className="mt-1 text-2xl font-extrabold text-slate-950">Micro-GCC Pod</h3>
            </div>
            <span className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
              3-5 people start
            </span>
          </div>

          <div className="mt-6 space-y-4">
            {[
              { role: "Backend / API Developer", detail: ".NET, Node, PHP, Python" },
              { role: "Frontend Developer", detail: "React, dashboards, portals" },
              { role: "QA and Delivery Support", detail: "Testing, demos, release checks" },
              { role: "Automation Specialist", detail: "CRM, WhatsApp, bots, reporting" },
            ].map((item, index) => (
              <div key={item.role} className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-sm font-bold text-white">
                  {index + 1}
                </div>
                <div>
                  <div className="font-bold text-slate-900">{item.role}</div>
                  <div className="text-sm text-slate-600">{item.detail}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {[
              { value: "2-4x", label: "cost efficient" },
              { value: "BOT Lite", label: "optional path" },
              { value: "Agile", label: "weekly delivery" },
            ].map((metric) => (
              <div key={metric.label} className="rounded-2xl border border-slate-100 bg-white p-4 text-center shadow-sm">
                <div className="text-xl font-extrabold text-primary">{metric.value}</div>
                <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </div>
</section>


      {/* Service Authority Section */}
<section className="relative overflow-hidden bg-white py-16 md:py-28">
  <div className="absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_10%_10%,rgba(59,130,246,0.12),transparent_55%),radial-gradient(760px_circle_at_90%_75%,rgba(20,184,166,0.10),transparent_55%)]" />
    <div className="absolute inset-0 opacity-[0.16] [background-image:radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.22)_1px,transparent_0)] [background-size:28px_28px]" />
  </div>

  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid lg:grid-cols-12 gap-10 items-start">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7 }}
        className="lg:col-span-5"
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-xs font-semibold tracking-widest text-blue-700">
          TOP SOFTWARE DEVELOPMENT SERVICES
        </span>

        <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-950 leading-tight">
          A service-first technology partner for serious business growth.
        </h2>

        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          Competitor sites emphasize broad service coverage, awards and quote-led conversion. We are making the homepage clearer: Variable Soft is a software service provider first, with product experience used as delivery strength for client projects.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a
            href="/getintouch"
            className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 font-semibold text-white shadow-lg hover:bg-slate-800 transition"
          >
            Get a Free Consultation
          </a>
          <a
            href="/service"
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 font-semibold text-slate-900 shadow-sm hover:border-primary/40 transition"
          >
            View All Services
          </a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="lg:col-span-7 grid sm:grid-cols-2 gap-5"
      >
        {[
          {
            title: "Best-fit development team",
            desc: "Choose project delivery, retainer support or Micro-GCC teams depending on your need.",
            icon: "fas fa-users-cog",
          },
          {
            title: "Top technology coverage",
            desc: "React, Node, PHP, .NET, Python, PostgreSQL, cloud, APIs and automation.",
            icon: "fas fa-layer-group",
          },
          {
            title: "AI-ready workflows",
            desc: "Add AI assistants, chatbots, lead scoring, reporting and business automation.",
            icon: "fas fa-wand-magic-sparkles",
          },
          {
            title: "Service-led support",
            desc: "Design, development, testing, launch, maintenance and iteration under one roof.",
            icon: "fas fa-headset",
          },
        ].map((item) => (
          <motion.div
            key={item.title}
            whileHover={{ y: -6 }}
            className="rounded-[26px] border border-slate-200 bg-white/85 p-6 shadow-[0_24px_70px_-45px_rgba(15,23,42,0.35)]"
          >
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-lg">
              <i className={`${item.icon} text-lg`} />
            </div>
            <h3 className="mt-5 text-xl font-extrabold text-slate-950">{item.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </div>
</section>
      {/* Services Section */}
<section ref={servicesRef} className="relative overflow-hidden py-24 bg-slate-950">
  {/* Clean premium background */}
  <div className="absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_20%,rgba(34,211,238,0.16),transparent_60%),radial-gradient(900px_circle_at_85%_70%,rgba(99,102,241,0.14),transparent_55%),linear-gradient(to_bottom,rgba(2,6,23,0.92),rgba(2,6,23,1))]" />
    <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.07)_1px,transparent_1px)] [background-size:52px_52px]" />
    <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_50%_15%,rgba(56,189,248,0.14),transparent_60%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_50%_45%,transparent_30%,rgba(0,0,0,0.72)_100%)]" />
  </div>

  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    {/* Header */}
    <motion.div
      initial={{ opacity: 0, y: 18, filter: "blur(8px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: false, amount: 0.02 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="text-center max-w-4xl mx-auto mb-14"
    >
      <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold tracking-widest text-white/70 backdrop-blur">
        <span className="relative h-2 w-2">
          <span className="absolute inset-0 rounded-full bg-cyan-300/70 animate-ping" />
          <span className="absolute inset-0 rounded-full bg-cyan-300" />
        </span>
        BEST SOFTWARE DEVELOPMENT SERVICES IN INDIA
      </span>

      <h2 className="mt-6 text-4xl lg:text-5xl font-extrabold text-white leading-tight">
        Services Built for Business Operations, {" "}
        <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-indigo-300 bg-clip-text text-transparent">
          Not Just Websites
        </span>
      </h2>

      <p className="mt-4 text-lg sm:text-xl text-white/70">
        From custom software and mobile apps to CRM, ERP, IVR, dialer systems, AI automation,
        SEO-ready websites and maintenance, we build services around real workflows, integrations and long-term support.
      </p>
    </motion.div>

    {/* CONTENT */}
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
      {services.map((service, index) => (
        <motion.div
          key={service.title}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: index * 0.2 }}
          className="relative rounded-[30px] border border-white/10 bg-white/[0.08] backdrop-blur-xl p-6 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.35)] hover:bg-white/[0.12] transition-all"
        >
          {/* Icon */}
          <div className={`h-16 w-16 rounded-2xl bg-gradient-to-r ${service.gradient} flex items-center justify-center mb-6`}>
            <i className={`${service.icon} text-white text-2xl`} />
          </div>

          <h3 className="text-2xl font-extrabold text-white mb-4">{service.title}</h3>
          <p className="text-white/70 mb-4">{service.description}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {service.tags.map((tag, idx) => (
              <span key={idx} className="text-xs font-semibold text-white/50 bg-white/[0.1] rounded-full px-3 py-1">
                {tag}
              </span>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-4 text-white/70 text-sm">
            <span className="font-semibold">Focus: </span>
            {service.stats}
          </div>
        </motion.div>
      ))}
    </div>
  </div>
</section>


      {/* Statistics Section */}

<section className="relative overflow-hidden bg-white py-24">
  {/* PREMIUM BACKGROUND */}
  <div className="absolute inset-0">
    {/* Soft Mesh */}
    <div className="absolute inset-0 bg-[radial-gradient(1100px_circle_at_20%_20%,rgba(59,130,246,0.14),transparent_60%),radial-gradient(900px_circle_at_85%_70%,rgba(34,211,238,0.12),transparent_55%),radial-gradient(900px_circle_at_50%_95%,rgba(99,102,241,0.10),transparent_60%)]" />

    {/* Subtle Grid */}
    <div className="absolute inset-0 opacity-[0.25] [background-image:linear-gradient(rgba(2,6,23,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(2,6,23,0.06)_1px,transparent_1px)] [background-size:54px_54px]" />

    {/* Vignette */}
    <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_50%_35%,transparent_45%,rgba(255,255,255,0.75)_100%)]" />

    {/* Top Fade */}
    <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white via-white/70 to-transparent" />
  </div>

  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid lg:grid-cols-12 gap-12 items-start">
      {/* LEFT CONTENT */}
      <motion.div
        initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        viewport={{ once: false, amount: 0.02 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-5"
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 backdrop-blur px-4 py-2 text-xs font-semibold tracking-widest text-primary shadow-lg">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          SOFTWARE DELIVERY PARTNER
        </span>

        <h2 className="mt-6 text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight">
          Services and teams{" "}
          <span className="bg-gradient-to-r from-primary via-indigo-500 to-blue-600 bg-clip-text text-transparent">
            built under one roof
          </span>{" "}
          and scalable solutions.
        </h2>

        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
          At Variable Soft, our core business is software development services. We build
          custom websites, web applications, mobile apps, CRM, ERP, IVR, dialer systems,
          AI automation and SEO-ready platforms with long-term support.
        </p>

        {/* TRUST CHIPS */}
        <div className="mt-8 flex flex-wrap gap-2">
          {["Best-fit Software Team", "Seamless Integration", "Fast & Reliable Support", "Top Web Solutions"].map(
            (t, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                whileHover={{ y: -2 }}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-md"
              >
                {t}
              </motion.span>
            )
          )}
        </div>

        {/* CTA */}
        <motion.a
          href="/getintouch"
          target="_blank"
          whileHover={{ y: -3, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="mt-10 inline-flex items-center gap-2 rounded-2xl bg-slate-900 text-white px-8 py-4 font-semibold shadow-xl hover:opacity-95 transition"
        >
          Get in Touch <span className="opacity-80">{"->"}</span>
        </motion.a>
      </motion.div>

      {/* RIGHT STATS GRID */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        viewport={{ once: true }}
        className="lg:col-span-7"
      >
        <div className="grid sm:grid-cols-2 gap-6">
          {companyStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.08 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.03 }}
              className="group relative rounded-[28px] border border-slate-200 bg-white/80 backdrop-blur p-6 shadow-[0_30px_90px_-55px_rgba(2,6,23,0.22)] overflow-hidden"
            >
              {/* hover glow */}
              <div className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-primary/16 via-cyan-400/14 to-indigo-500/14 blur-xl" />

              <div className="relative">
                <div className="flex items-start justify-between">
                  {/* icon */}
                  <div className="h-12 w-12 rounded-2xl border border-slate-200 bg-white flex items-center justify-center shadow-md">
                    <motion.i
                      className={`${stat.icon} text-xl text-slate-900`}
                      animate={{ y: [0, -2, 0] }}
                      transition={{ duration: 2.4, repeat: Infinity }}
                    />
                  </div>

                  <span className="text-xs text-slate-600 rounded-full border border-slate-200 bg-white px-3 py-1.5">
                    Verified
                  </span>
                </div>

                <div className="mt-5">
                  <div className="text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
                    <motion.span
                      initial={{ opacity: 0, y: 10, scale: 0.9 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                      {stat.value}
                    </motion.span>
                  </div>

                  <div className="mt-2 text-base font-semibold text-slate-800">
                    {stat.label}
                  </div>

                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {stat.description}
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-primary" />
                    Growing steadily
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* BOTTOM STRIP */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-8 rounded-[28px] border border-slate-200 bg-white/80 backdrop-blur p-6 shadow-lg"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-slate-900 font-semibold">
              Built for startups & enterprises
              <span className="text-slate-600 font-normal"> - from strategy to scale.</span>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2">
                <span className="h-2 w-2 rounded-full bg-primary" />
                On-time delivery
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2">
                <span className="h-2 w-2 rounded-full bg-cyan-500" />
                Quality assured
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </div>
</section>

      {/* CTA Section */}
<section className="relative overflow-hidden bg-slate-950 py-20 sm:py-28">
  {/* BACKGROUND LAYERS */}
  <div className="absolute inset-0 pointer-events-none">
    {/* Aurora Clouds */}
    <motion.div
      aria-hidden
      className="absolute -top-40 sm:-top-56 left-1/2 h-[420px] sm:h-[620px] w-[900px] sm:w-[1200px] -translate-x-1/2 rounded-full blur-3xl opacity-50"
      style={{
        background:
          "conic-gradient(from_180deg_at_50%_50%,rgba(34,211,238,0),rgba(34,211,238,0.35),rgba(59,130,246,0.28),rgba(99,102,241,0.22),rgba(34,211,238,0))",
      }}
      animate={{ rotate: 360, y: [0, 12, 0] }}
      transition={{
        rotate: { duration: 55, repeat: Infinity, ease: "linear" },
        y: { duration: 9, repeat: Infinity, ease: "easeInOut" },
      }}
    />

    <motion.div
      aria-hidden
      className="absolute -bottom-40 sm:-bottom-64 left-1/2 h-[420px] sm:h-[640px] w-[900px] sm:w-[1200px] -translate-x-1/2 rounded-full blur-3xl opacity-40"
      style={{
        background:
          "conic-gradient(from_0deg_at_50%_50%,rgba(59,130,246,0),rgba(99,102,241,0.30),rgba(34,211,238,0.25),rgba(59,130,246,0.20),rgba(59,130,246,0))",
      }}
      animate={{ rotate: -360, y: [0, -10, 0] }}
      transition={{
        rotate: { duration: 70, repeat: Infinity, ease: "linear" },
        y: { duration: 11, repeat: Infinity, ease: "easeInOut" },
      }}
    />

    {/* Grid */}
    <div className="absolute inset-0 opacity-[0.14] [background-image:radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.20)_1px,transparent_0)] [background-size:28px_28px]" />

    {/* Vignette */}
    <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_50%_40%,transparent_30%,rgba(0,0,0,0.85)_100%)]" />
  </div>

  <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}   // ðŸ”¥ FIX
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-[28px] sm:rounded-[36px] border border-white/10 bg-white/5 backdrop-blur-2xl
        shadow-[0_40px_120px_-60px_rgba(0,0,0,0.95)] overflow-hidden"
    >
      {/* Glowing Border */}
      <div className="absolute -inset-px rounded-[36px] bg-gradient-to-r from-cyan-400/35 via-blue-500/30 to-indigo-500/30 blur-2xl opacity-70" />
      <div className="absolute inset-0 bg-slate-950/55" />

      {/* CONTENT */}
      <div className="relative p-8 sm:p-16 text-center">
        {/* Badge */}
        <motion.span
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2 text-xs font-semibold tracking-widest text-white/70"
        >
          <span className="relative h-2 w-2">
            <span className="absolute inset-0 rounded-full bg-cyan-300 animate-ping opacity-60" />
            <span className="absolute inset-0 rounded-full bg-cyan-300" />
          </span>
          LET&apos;S BUILD SOMETHING EXTRAORDINARY
        </motion.span>

        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="mt-8 text-3xl sm:text-5xl font-extrabold text-white leading-tight"
        >
          Ready to{" "}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-indigo-300 bg-clip-text text-transparent">
              Transform
            </span>
            <span className="absolute -bottom-2 left-0 h-[3px] w-full rounded-full bg-gradient-to-r from-cyan-300 via-blue-300 to-indigo-300 opacity-80" />
          </span>{" "}
          Your Business?
        </motion.h2>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-6 text-base sm:text-xl text-white/70 max-w-2xl mx-auto"
        >
          From strategy to execution - we design, build, and scale innovative software
          solutions to help businesses thrive in the digital world.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href="/getintouch"
            className="inline-flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 px-8 py-4 text-slate-950 font-bold shadow-lg"
          >
            Start Your Project
          </a>

          <a
            href="/portfolio"
            className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/20 bg-white/5 px-8 py-4 text-white font-semibold"
          >
            View Our Work
          </a>
        </motion.div>
      </div>
    </motion.div>
  </div>
</section>

      <Footer />
    </div>
  );
}

